import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Linkedin,
  Users,
  Award,
  ShieldCheck,
  X,
  Quote,
  Heart,
  Target,
} from "lucide-react";

type TeamMember = {
  id: number;
  name: string;
  designation: string;
  category: string;
  bio?: string;
  profile_image?: string;
  linkedin_url?: string;
};

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/our-team`;
const defaultImage = `${siteUrl}/logo512.png`;
const backendUrl = "https://www.sevaafoundation.com/seva-backend";

const OurTeam = () => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [isBioModalOpen, setIsBioModalOpen] = useState(false);

  useEffect(() => {
const fetchTeam = async () => {
  try {
    // Timestamp add karne se browser hamesha fresh data layega
    const res = await fetch(`${backendUrl}/api/team/list_members.php?t=${new Date().getTime()}`);
    const data = await res.json();

    if (data.success) {
      setMembers(data.members);
    }
  } catch (error) {
    console.error("Failed to load team", error);
  } finally {
    setIsLoading(false);
  }
};

    fetchTeam();
  }, []);

  const getMemberImage = (member: TeamMember, size = "400x400") => {
    if (member.profile_image) {
      return `${backendUrl}${member.profile_image}`;
    }
    return `https://via.placeholder.com/${size}?text=No+Image`;
  };

  const openBioModal = (member: TeamMember) => {
    setSelectedMember(member);
    setIsBioModalOpen(true);
  };

  // --- DATA GROUPING LOGIC (Bulletproof case & space handling) ---
  const cleanCategory = (cat?: string) => (cat || "").trim().toLowerCase();

  // 1. Leadership Team (Merged Board of Directors & Governing Members)
  const leadershipMembers = members.filter(
    (m) => {
      const cat = cleanCategory(m.category);
      return (
        cat === "board of directors" ||
        cat === "governing members" ||
        cat === "board of directors / governing members"
      );
    }
  );

  // 2. Management Team (Large Photo Size - Fixed)
  const managementMembers = members.filter(
    (m) => cleanCategory(m.category) === "management team"
  );

  // 3. Executive Team & Others (Small Photo Size)
  const smallCardTeams = members.filter(
    (m) => {
      const cat = cleanCategory(m.category);
      return (
        cat !== "board of directors" &&
        cat !== "governing members" &&
        cat !== "board of directors / governing members" &&
        cat !== "management team"
      );
    }
  );

  // Group all small card members dynamically by their exact category name
  const groupedOtherMembers = smallCardTeams.reduce((acc, member) => {
    // Preserve the original case for the heading, just trim spaces
    const cat = member.category?.trim() || "Other Members";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(member);
    return acc;
  }, {} as Record<string, TeamMember[]>);

  // --- SCHEMAS ---

  const teamPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "Our Team | Sevaa Foundation",
    alternateName: "Sevaa Foundation Leadership Team",
    url: pageUrl,
    inLanguage: ["en-IN", "hi-IN"],
    description:
      "Meet the leadership team, board of directors, governing members and executive team of Sevaa Foundation, a registered NGO working for education, healthcare, women empowerment, hunger support and social welfare.",
    isPartOf: {
      "@type": "WebSite",
      name: "Sevaa Foundation",
      url: siteUrl,
    },
    publisher: {
      "@type": "NGO",
      name: "Sevaa Foundation",
      alternateName: "Swadesh Sevaa Charitable Foundation",
      url: siteUrl,
      logo: defaultImage,
      areaServed: {
        "@type": "Country",
        name: "India",
      },
    },
    about: [
      "NGO Team",
      "NGO Leadership",
      "Board of Directors",
      "Governing Members",
      "Executive Team",
      "Social Welfare Leadership",
      "Charitable Trust Team",
      "Sevaa Foundation Team",
    ],
    mainEntity: {
      "@type": "Organization",
      name: "Sevaa Foundation",
      alternateName: "Swadesh Sevaa Charitable Foundation",
      url: siteUrl,
      logo: defaultImage,
      member: members.map((member) => ({
        "@type": "Person",
        name: member.name,
        jobTitle: member.designation,
        description:
          member.bio ||
          `${member.name} is associated with Sevaa Foundation as ${member.designation}.`,
        image: getMemberImage(member),
        sameAs: member.linkedin_url ? [member.linkedin_url] : undefined,
      })),
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: siteUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Our Team",
        item: pageUrl,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>
          Our Team | Sevaa Foundation Leadership, Board Members & NGO Team
        </title>
        {/* Helmet Meta Tags Kept Same */}
      </Helmet>

      <main className="bg-slate-50 min-h-screen font-sans selection:bg-emerald-500 selection:text-white">
        <section
          aria-labelledby="our-team-heading"
          className="relative bg-slate-900 text-white py-32 px-6 overflow-hidden"
        >
          <div
            className="absolute top-0 right-0 -mr-20 -mt-20 w-[30rem] h-[30rem] rounded-full bg-emerald-500/10 blur-[100px]"
            aria-hidden="true"
          ></div>
          <div
            className="absolute bottom-0 left-0 -ml-20 -mb-20 w-[20rem] h-[20rem] rounded-full bg-orange-500/10 blur-[80px]"
            aria-hidden="true"
          ></div>
          <div className="relative z-10 max-w-5xl mx-auto text-center">
            <span className="text-emerald-400 text-sm font-bold uppercase tracking-[0.3em] mb-4 block">
              Swadesh Sevaa Foundation
            </span>
            <h1
              id="our-team-heading"
              className="text-5xl md:text-7xl font-black mb-6 leading-tight"
            >
              Meet The{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-200">
                Visionaries
              </span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto font-light leading-relaxed">
              Meet Sevaa Foundation&apos;s board of directors, governing
              members, executive team and changemakers dedicated to creating
              lasting social impact through education, healthcare, women
              empowerment, hunger support and social welfare.
            </p>
          </div>
        </section>

        <section
          aria-labelledby="team-values-heading"
          className="max-w-7xl mx-auto px-6 -mt-16 relative z-20 mb-20"
        >
          <h2 id="team-values-heading" className="sr-only">
            Sevaa Foundation Team Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: "Transparency",
                desc: "100% accountability in our operations, leadership, and fund utilization.",
                icon: <ShieldCheck size={32} />,
                color: "text-emerald-500",
                bg: "bg-emerald-50",
              },
              {
                title: "Compassion",
                desc: "Driven by deep empathy to uplift and empower the most vulnerable communities.",
                icon: <Heart size={32} />,
                color: "text-orange-500",
                bg: "bg-orange-50",
              },
              {
                title: "Impact",
                desc: "Focused on sustainable, long-term solutions, not just temporary quick fixes.",
                icon: <Target size={32} />,
                color: "text-blue-500",
                bg: "bg-blue-50",
              },
            ].map((value) => (
              <article
                key={value.title}
                className="bg-white p-8 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 flex flex-col items-center text-center hover:-translate-y-2 transition-transform duration-300"
              >
                <div
                  className={`w-20 h-20 rounded-2xl ${value.bg} ${value.color} flex items-center justify-center mb-6`}
                  aria-hidden="true"
                >
                  {value.icon}
                </div>
                <h3 className="text-xl font-black text-slate-800 mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {value.desc}
                </p>
              </article>
            ))}
          </div>
        </section>

        {isLoading ? (
          <section className="min-h-[70vh] flex items-center justify-center text-emerald-600 font-bold tracking-widest uppercase">
            Loading Visionaries...
          </section>
        ) : (
          <div className="max-w-7xl mx-auto px-6 py-10 space-y-32">
            
            {/* 1. LEADERSHIP TEAM */}
            {leadershipMembers.length > 0 && (
              <section aria-labelledby="leadership-heading">
                <div className="flex items-center justify-center gap-4 mb-16">
                  <Award className="text-emerald-600" size={36} />
                  <h2
                    id="leadership-heading"
                    className="text-4xl md:text-5xl font-black text-slate-800 text-center"
                  >
                    Board of Directors / Governing Members
                  </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                  {leadershipMembers.map((member) => (
                    <article key={member.id} className="bg-transparent group">
                      <div className="w-full aspect-square rounded-3xl overflow-hidden mb-6 relative shadow-lg shadow-slate-200/50">
                        {member.bio && (
                          <div className="absolute inset-0 bg-slate-900/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 flex flex-col items-center justify-center p-6 text-center backdrop-blur-sm">
                            <Quote className="text-white/30 mb-2" size={32} />
                            <p className="text-white text-xs line-clamp-4 font-light mb-4">
                              {member.bio}
                            </p>
                            <button
                              onClick={() => openBioModal(member)}
                              className="px-5 py-2.5 bg-white text-slate-900 text-xs font-bold rounded-full hover:bg-orange-500 hover:text-white transition-colors"
                              aria-label={`Read full bio of ${member.name}`}
                            >
                              Read Full Bio
                            </button>
                          </div>
                        )}
                        <img
                          src={getMemberImage(member)}
                          alt={`${member.name}, ${member.designation} at Sevaa Foundation`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="text-center px-2">
                        <h3 className="text-xl font-black text-slate-800 mb-1">
                          {member.name}
                        </h3>
                        <p className="text-orange-600 font-bold text-xs uppercase tracking-wider mb-3">
                          {member.designation}
                        </p>
                        {member.linkedin_url && (
                          <a
                            href={member.linkedin_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-block text-slate-400 hover:text-blue-600 transition-colors"
                            aria-label={`View ${member.name} LinkedIn profile`}
                          >
                            <Linkedin size={20} />
                          </a>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {/* 2. MANAGEMENT TEAM (Same Large Photo Size as Leadership) */}
            {managementMembers.length > 0 && (
              <section aria-labelledby="management-heading">
                <div className="flex items-center justify-center gap-4 mb-16">
                  <ShieldCheck className="text-orange-500" size={36} />
                  <h2
                    id="management-heading"
                    className="text-4xl md:text-5xl font-black text-slate-800 text-center"
                  >
                    Management Team
                  </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                  {managementMembers.map((member) => (
                    <article key={member.id} className="bg-transparent group">
                      <div className="w-full aspect-square rounded-3xl overflow-hidden mb-6 relative shadow-lg shadow-slate-200/50">
                        {member.bio && (
                          <div className="absolute inset-0 bg-slate-900/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 flex flex-col items-center justify-center p-6 text-center backdrop-blur-sm">
                            <Quote className="text-white/30 mb-2" size={32} />
                            <p className="text-white text-xs line-clamp-4 font-light mb-4">
                              {member.bio}
                            </p>
                            <button
                              onClick={() => openBioModal(member)}
                              className="px-5 py-2.5 bg-white text-slate-900 text-xs font-bold rounded-full hover:bg-orange-500 hover:text-white transition-colors"
                              aria-label={`Read full bio of ${member.name}`}
                            >
                              Read Full Bio
                            </button>
                          </div>
                        )}
                        <img
                          src={getMemberImage(member)}
                          alt={`${member.name}, ${member.designation} at Sevaa Foundation`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="text-center px-2">
                        <h3 className="text-xl font-black text-slate-800 mb-1">
                          {member.name}
                        </h3>
                        <p className="text-orange-600 font-bold text-xs uppercase tracking-wider mb-3">
                          {member.designation}
                        </p>
                        {member.linkedin_url && (
                          <a
                            href={member.linkedin_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-block text-slate-400 hover:text-blue-600 transition-colors"
                            aria-label={`View ${member.name} LinkedIn profile`}
                          >
                            <Linkedin size={20} />
                          </a>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {/* 3. DYNAMIC SMALL CARD SECTIONS (Executive Team & Advocate Team etc.) */}
            {Object.entries(groupedOtherMembers).map(([categoryName, groupMembers]) => (
              <section key={categoryName} aria-labelledby={`heading-${categoryName.replace(/\s+/g, '-')}`}>
                <div className="flex flex-col items-center justify-center gap-3 mb-12 text-center">
                  <div className="p-4 bg-blue-50 text-blue-600 rounded-2xl mb-2">
                    <Users size={32} />
                  </div>
                  <h2
                    id={`heading-${categoryName.replace(/\s+/g, '-')}`}
                    className="text-4xl md:text-5xl font-black text-slate-800"
                  >
                    {categoryName}
                  </h2>
                </div>

                <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 p-8 md:p-12 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-slate-50 rounded-full -mr-32 -mt-32 -z-0"></div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
                    {groupMembers.map((member) => (
                      <article
                        key={member.id}
                        className="flex items-start gap-5 p-5 rounded-2xl bg-white border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all cursor-pointer group"
                        onClick={() => member.bio && openBioModal(member)}
                        aria-label={`View profile of ${member.name}`}
                      >
                        <img
                          src={
                            member.profile_image
                              ? `${backendUrl}${member.profile_image}`
                              : "https://via.placeholder.com/150x150?text=User"
                          }
                          alt={`${member.name}, ${member.designation} at Sevaa Foundation`}
                      
                          className="w-24 h-24 rounded-full object-cover shadow-sm border-2 border-slate-100 shrink-0 group-hover:border-blue-200 transition-colors"
                          loading="lazy"
                        />

                        <div className="pt-2">
                          <h3 className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors leading-tight mb-1">
                            {member.name}
                          </h3>

                          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                            {member.designation}
                          </p>

                          {member.bio ? (
                            <span className="text-xs text-blue-600 font-semibold group-hover:underline">
                              View Profile →
                            </span>
                          ) : (
                            member.linkedin_url && (
                              <a
                                href={member.linkedin_url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(event) => event.stopPropagation()}
                                className="text-slate-300 hover:text-blue-600"
                                aria-label={`View ${member.name} LinkedIn profile`}
                              >
                                <Linkedin size={18} />
                              </a>
                            )
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </section>
            ))}

          </div>
        )}

        {/* Footer CTA & Modals Kept Same */}
        <section className="bg-slate-900 py-24 px-6 text-center mt-20 relative overflow-hidden">
          <div
            className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"
            aria-hidden="true"
          ></div>
          <div className="max-w-3xl mx-auto relative z-10">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Want to be part of the change?
            </h2>
            <p className="text-slate-400 text-lg mb-10">
              We are always looking for passionate volunteers, partners, and
              donors to join our mission on the ground. Your support makes
              everything we do possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/donate"
                className="px-8 py-4 bg-emerald-500 text-white font-bold rounded-full hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-500/30"
              >
                Support Our Mission
              </a>
              <a
                href="/contact"
                className="px-8 py-4 bg-transparent border-2 border-slate-700 text-white font-bold rounded-full hover:border-slate-500 transition-colors"
              >
                Contact The Team
              </a>
            </div>
          </div>
        </section>

        {isBioModalOpen && selectedMember && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
            onClick={() => setIsBioModalOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="member-bio-title"
          >
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md transition-opacity"></div>
            <div
              className="relative bg-white w-full max-w-3xl rounded-[2rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-fade-in-up"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                onClick={() => setIsBioModalOpen(false)}
                className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2.5 bg-slate-100/80 hover:bg-slate-900 hover:text-white text-slate-600 rounded-full transition-colors z-20 backdrop-blur"
                aria-label="Close team member bio modal"
              >
                <X size={20} strokeWidth={2.5} />
              </button>

              <div className="bg-slate-50 border-b border-slate-100 p-6 sm:p-10 flex flex-col sm:flex-row items-center sm:items-start gap-6">
                <img
                  src={getMemberImage(selectedMember)}
                  alt={`${selectedMember.name}, ${selectedMember.designation} biography at Sevaa Foundation`}
                  className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover shadow-lg border-4 border-white shrink-0"
                />
                <div className="text-center sm:text-left pt-2">
                  <h2
                    id="member-bio-title"
                    className="text-3xl sm:text-4xl font-black text-slate-900 mb-2"
                  >
                    {selectedMember.name}
                  </h2>
                  <p className="text-emerald-600 font-bold uppercase tracking-widest text-sm mb-4">
                    {selectedMember.designation}
                  </p>
                  <span className="inline-block bg-slate-200 text-slate-600 text-xs font-bold px-3 py-1 rounded-full uppercase">
                    {selectedMember.category}
                  </span>
                </div>
              </div>

              <div className="p-6 sm:p-10 overflow-y-auto custom-scrollbar relative">
                <Quote className="absolute top-6 left-6 text-slate-100 w-24 h-24 -z-10" />
                <div className="relative z-10 text-slate-600 text-base sm:text-lg leading-loose whitespace-pre-wrap font-medium">
                  {selectedMember.bio}
                </div>
                {selectedMember.linkedin_url && (
                  <div className="mt-10 pt-6 border-t border-slate-100">
                    <a
                      href={selectedMember.linkedin_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-blue-50 text-blue-600 font-bold rounded-xl hover:bg-blue-600 hover:text-white transition-colors"
                      aria-label={`View ${selectedMember.name} LinkedIn profile`}
                    >
                      <Linkedin size={20} /> View LinkedIn Profile
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default OurTeam;